export type TiffErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_large_image'
  | 'invalid_signature'
  | 'unsupported_variant'
  | 'no_images'
  | 'corrupt_image'
  | 'decode_failed'
  | 'memory_failed'
  | 'canvas_error'
  | 'conversion_failed'

export interface TiffErrors {
  unsupported: string
  tooLarge: string
  tooLargeImage: string
  invalidSignature: string
  unsupportedVariant: string
  noImages: string
  corrupt: string
  decode: string
  memory: string
  canvas: string
  conversion: string
  generic: string
}

export interface TiffDecodeResult {
  blob: Blob
  frames: number
  width: number
  height: number
}

export const MAX_TIFF_FILE_SIZE = 50 * 1024 * 1024
export const MAX_TIFF_PIXELS = 36_000_000
export const MAX_TIFF_DIMENSION = 16_384
export const MAX_TIFF_RGBA_BYTES = 64 * 1024 * 1024

const ACCEPTED_EXTENSIONS = ['.tif', '.tiff']
const ACCEPTED_TYPES = ['image/tiff', 'image/x-tiff']
const CLASSIC_TIFF_MAGIC = 42
const BIG_TIFF_MAGIC = 43
const COMMON_SAFE_COMPRESSIONS = new Set([1])
const CCITT_COMPRESSIONS = new Set([3, 4])
const JPEG_COMPRESSIONS = new Set([6, 7])
const THUNDERSCAN_COMPRESSION = 32809
const MAX_TIFF_IFD_CHAIN_LENGTH = 256
const MAX_TIFF_IFD_ENTRIES = 4096
const MAX_TIFF_IFD_VALUE_BYTES = 4 * 1024 * 1024
const MAX_TIFF_DATA_CHUNKS = 4096
const MAX_TIFF_DECODE_BYTES = 128 * 1024 * 1024
const activeTiffOperations = new Set<PendingTiffOperation>()

class PendingTiffOperation {
  worker: Worker | null = null
  cancelled = false
  settleCancellation: (() => void) | null = null

  cancel(): void {
    this.cancelled = true
    if (this.settleCancellation) this.settleCancellation()
    else this.worker?.terminate()
    this.settleCancellation = null
    activeTiffOperations.delete(this)
  }
}

const TIFF_TYPE_SIZES: Readonly<Record<number, number>> = {
  1: 1, // BYTE
  2: 1, // ASCII
  3: 2, // SHORT
  4: 4, // LONG
  5: 8, // RATIONAL
  6: 1, // SBYTE
  7: 1, // UNDEFINED
  8: 2, // SSHORT
  9: 4, // SLONG
  10: 8, // SRATIONAL
  11: 4, // FLOAT
  12: 8, // DOUBLE
}

// UTIF recursively follows these metadata payloads while reading an IFD. The
// preflight only models classic LONG offset arrays, so reject the additional
// recursive paths rather than letting them reach the synchronous decoder.
const NESTED_IFD_TAGS = new Set([330, 34665, 34853, 40965, 37500, 50740])

export interface TiffIfd {
  t256?: unknown
  t257?: unknown
  t258?: unknown
  t259?: unknown
  t262?: unknown
  t277?: unknown
  t284?: unknown
  t266?: unknown
  t274?: unknown
  t338?: unknown
  t339?: unknown
  t273?: unknown
  t278?: unknown
  t279?: unknown
  t317?: unknown
  t324?: unknown
  t325?: unknown
  t320?: unknown
  t322?: unknown
  t323?: unknown
}

type TiffHeader = 'classic' | 'big_tiff' | 'invalid'

export function readTiffHeader(buffer: ArrayBuffer): TiffHeader {
  if (buffer.byteLength < 4) return 'invalid'
  const bytes = new Uint8Array(buffer, 0, 4)
  const littleEndian = bytes[0] === 0x49 && bytes[1] === 0x49
  const bigEndian = bytes[0] === 0x4d && bytes[1] === 0x4d
  if (!littleEndian && !bigEndian) return 'invalid'

  const thirdByte = bytes[2] ?? 0
  const fourthByte = bytes[3] ?? 0
  const magic = littleEndian
    ? thirdByte | (fourthByte << 8)
    : (thirdByte << 8) | fourthByte
  if (magic === CLASSIC_TIFF_MAGIC) return 'classic'
  if (magic === BIG_TIFF_MAGIC) return 'big_tiff'
  return 'invalid'
}

function validateClassicTiffIfdChain(buffer: ArrayBuffer, errors: TiffErrors): number {
  if (buffer.byteLength < 8) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }
  const view = new DataView(buffer)
  const bytes = new Uint8Array(buffer, 0, 2)
  const littleEndian = bytes[0] === 0x49 && bytes[1] === 0x49
  const endianness = littleEndian
  const pendingIfdOffsets = [view.getUint32(4, endianness)]
  if (pendingIfdOffsets[0] === 0) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }
  const visitedOffsets = new Set<number>()
  let parsedValueBytes = 0
  let parsedIfdEntries = 0
  let firstImageCompression = 1

  for (let index = 0; pendingIfdOffsets.length > 0; index += 1) {
    const ifdOffset = pendingIfdOffsets.shift() ?? 0
    if (ifdOffset === 0) continue
    if (
      index >= MAX_TIFF_IFD_CHAIN_LENGTH ||
      visitedOffsets.has(ifdOffset) ||
      ifdOffset > buffer.byteLength - 2
    ) {
      throw new TiffConversionError(errors.corrupt, 'corrupt_image')
    }
    visitedOffsets.add(ifdOffset)

    const entryCount = view.getUint16(ifdOffset, endianness)
    parsedIfdEntries += entryCount
    if (parsedIfdEntries > MAX_TIFF_IFD_ENTRIES) {
      throw new TiffConversionError(errors.corrupt, 'corrupt_image')
    }
    const entriesBytes = entryCount * 12
    const entriesEnd = ifdOffset + 2 + entriesBytes
    const nextOffsetPosition = entriesEnd
    if (entriesEnd > buffer.byteLength - 4) {
      throw new TiffConversionError(errors.corrupt, 'corrupt_image')
    }

    const nestedIfdOffsets: number[] = []
    const dataRanges = new Map<number, number[]>()
    for (let entryIndex = 0; entryIndex < entryCount; entryIndex += 1) {
      const entryOffset = ifdOffset + 2 + entryIndex * 12
      const tag = view.getUint16(entryOffset, endianness)
      const type = view.getUint16(entryOffset + 2, endianness)
      const count = view.getUint32(entryOffset + 4, endianness)
      const typeSize = TIFF_TYPE_SIZES[type]
      if (typeSize === undefined) {
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }
      if (count > Math.floor(Number.MAX_SAFE_INTEGER / typeSize)) {
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }

      const valueBytes = count * typeSize
      if (valueBytes > MAX_TIFF_IFD_VALUE_BYTES) {
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }
      parsedValueBytes += valueBytes
      if (parsedValueBytes > MAX_TIFF_IFD_VALUE_BYTES) {
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }

      const valueOffset = entryOffset + 8
      if (tag === 37500 || tag === 50740) {
        // UTIF uses different, decoder-internal offsets for these recursive
        // metadata paths. Do not try to approximate that traversal here.
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }
      if (NESTED_IFD_TAGS.has(tag) && (type !== 4 || count === 0 || count > MAX_TIFF_IFD_CHAIN_LENGTH)) {
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }
      if ([273, 279, 324, 325].includes(tag)) {
        if (![3, 4].includes(type)) {
          throw new TiffConversionError(errors.corrupt, 'corrupt_image')
        }
        if (count === 0 || count > MAX_TIFF_DATA_CHUNKS) {
          throw new TiffConversionError(errors.corrupt, 'corrupt_image')
        }
        const valuesOffset = valueBytes <= 4 ? valueOffset : view.getUint32(valueOffset, endianness)
        if (valueBytes > 4 && (valuesOffset > buffer.byteLength || valueBytes > buffer.byteLength - valuesOffset)) {
          throw new TiffConversionError(errors.corrupt, 'corrupt_image')
        }
        const values: number[] = []
        for (let valueIndex = 0; valueIndex < count; valueIndex += 1) {
          const itemOffset = valueBytes <= 4
            ? valueOffset + valueIndex * typeSize
            : valuesOffset + valueIndex * typeSize
          values.push(type === 3
            ? view.getUint16(itemOffset, endianness)
            : view.getUint32(itemOffset, endianness))
        }
        dataRanges.set(tag, values)
      }
      if (index === 0 && tag === 259 && count === 1) {
        if (type === 3) firstImageCompression = view.getUint16(valueOffset, endianness)
        if (type === 4) firstImageCompression = view.getUint32(valueOffset, endianness)
      }
      if (valueBytes > 4) {
        const payloadOffset = view.getUint32(valueOffset, endianness)
        if (payloadOffset > buffer.byteLength || valueBytes > buffer.byteLength - payloadOffset) {
          throw new TiffConversionError(errors.corrupt, 'corrupt_image')
        }
        if (NESTED_IFD_TAGS.has(tag) && type === 4) {
          if (count > MAX_TIFF_IFD_CHAIN_LENGTH) {
            throw new TiffConversionError(errors.corrupt, 'corrupt_image')
          }
          for (let offsetIndex = 0; offsetIndex < count; offsetIndex += 1) {
            nestedIfdOffsets.push(view.getUint32(payloadOffset + offsetIndex * 4, endianness))
          }
        }
      } else if (NESTED_IFD_TAGS.has(tag) && type === 4 && count === 1) {
        nestedIfdOffsets.push(view.getUint32(valueOffset, endianness))
      }
    }

    const nextOffset = view.getUint32(nextOffsetPosition, endianness)
    for (const [offsetTag, countTag] of [[273, 279], [324, 325]] as const) {
      const offsets = dataRanges.get(offsetTag)
      const counts = dataRanges.get(countTag)
      if (offsets === undefined && counts === undefined) continue
      if (
        offsets === undefined ||
        counts === undefined ||
        offsets.length === 0 ||
        offsets.length !== counts.length ||
        offsets.length > MAX_TIFF_DATA_CHUNKS
      ) {
        throw new TiffConversionError(errors.corrupt, 'corrupt_image')
      }
      let totalBytes = 0
      for (let rangeIndex = 0; rangeIndex < offsets.length; rangeIndex += 1) {
        const offset = offsets[rangeIndex] ?? -1
        const count = counts[rangeIndex] ?? -1
        if (offset < 0 || count <= 0 || offset > buffer.byteLength || count > buffer.byteLength - offset) {
          throw new TiffConversionError(errors.corrupt, 'corrupt_image')
        }
        totalBytes += count
        if (totalBytes > MAX_TIFF_FILE_SIZE) {
          throw new TiffConversionError(errors.corrupt, 'corrupt_image')
        }
      }
    }
    if (nestedIfdOffsets.some((offset) => offset === 0)) {
      throw new TiffConversionError(errors.corrupt, 'corrupt_image')
    }
    if (nextOffset !== 0) pendingIfdOffsets.push(nextOffset)
    pendingIfdOffsets.push(...nestedIfdOffsets)
  }

  return firstImageCompression
}

function looksLikeMemoryFailure(error: unknown): boolean {
  if (error instanceof RangeError) return true
  const message = error instanceof Error ? error.message.toLowerCase() : ''
  return /memory|allocation|array buffer|typed array|out of memory/.test(message)
}

export class TiffConversionError extends Error {
  constructor(
    message: string,
    readonly code: TiffErrorCode,
  ) {
    super(message)
  }
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function isTiffLike(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

export function outputFilename(inputName: string): string {
  const base = inputName.replace(/\.[^.]+$/, '')
  return `${base || 'converted'}.jpg`
}

export function uniqueOutputFilename(inputName: string, usedNames: Set<string>): string {
  const filename = outputFilename(inputName)
  if (!usedNames.has(filename)) {
    usedNames.add(filename)
    return filename
  }

  const base = filename.replace(/\.jpg$/i, '')
  let counter = 2
  let next = `${base}-${counter}.jpg`
  while (usedNames.has(next)) {
    counter += 1
    next = `${base}-${counter}.jpg`
  }
  usedNames.add(next)
  return next
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function validateTiffFile(
  file: File,
  errors: TiffErrors,
): { message: string; code: TiffErrorCode } | null {
  if (!isTiffLike(file)) {
    return { message: errors.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_TIFF_FILE_SIZE) {
    return { message: errors.tooLarge, code: 'too_large' }
  }
  return null
}

function hasImageTags(ifd: TiffIfd): boolean {
  return ifd.t256 !== undefined && ifd.t257 !== undefined
}

function readNumericTag(value: unknown): number[] | null {
  return Array.isArray(value) && value.every((item) => typeof item === 'number')
    ? value
    : null
}

function readFirstNumericTag(value: unknown, fallback: number): number {
  if (value === undefined) return fallback
  return readNumericTag(value)?.[0] ?? Number.NaN
}

function readDimension(value: unknown): number | null {
  const dimension = readNumericTag(value)?.[0]
  return typeof dimension === 'number' && Number.isFinite(dimension) && Number.isInteger(dimension)
    ? dimension
    : null
}

function validateDimensionsBeforeDecode(
  ifd: TiffIfd,
  errors: TiffErrors,
): { width: number; height: number } {
  const width = readDimension(ifd.t256)
  const height = readDimension(ifd.t257)
  if (width === null || height === null || width <= 0 || height <= 0) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }
  if (width > MAX_TIFF_DIMENSION || height > MAX_TIFF_DIMENSION || width > MAX_TIFF_PIXELS / height) {
    throw new TiffConversionError(errors.tooLargeImage, 'too_large_image')
  }
  return { width, height }
}

function validateDecodeMemoryBudget(
  ifd: TiffIfd,
  dimensions: { width: number; height: number },
  errors: TiffErrors,
): void {
  const layout = readUniformPixelLayout(ifd)
  if (!layout) return
  const predictor = readFirstNumericTag(ifd.t317, 1)
  if (!Number.isSafeInteger(predictor) || predictor <= 0 || predictor !== 1) {
    throw new TiffConversionError(errors.unsupportedVariant, 'unsupported_variant')
  }
  const bytesPerSample = Math.ceil(layout.bitsPerSample / 8)
  const decodedBytes = dimensions.width * dimensions.height * layout.samples * bytesPerSample
  if (decodedBytes > MAX_TIFF_DECODE_BYTES) {
    throw new TiffConversionError(errors.tooLargeImage, 'too_large_image')
  }
  const rgbaBytes = dimensions.width * dimensions.height * 4
  if (!Number.isSafeInteger(rgbaBytes) || rgbaBytes > MAX_TIFF_RGBA_BYTES) {
    throw new TiffConversionError(errors.tooLargeImage, 'too_large_image')
  }

  // Tile decoding is intentionally outside the accepted subset. UTIF's tile
  // edge and packed-row behavior needs a separate bounded adapter.
}

function validateStorageLayout(
  ifd: TiffIfd,
  errors: TiffErrors,
): void {
  const hasStripTag = ifd.t273 !== undefined || ifd.t279 !== undefined
  const hasTileTag = ifd.t322 !== undefined || ifd.t323 !== undefined || ifd.t324 !== undefined || ifd.t325 !== undefined
  if (!hasStripTag || hasTileTag) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }

  const hasCompleteStrips = ifd.t273 !== undefined && ifd.t279 !== undefined
  const hasCompleteTiles = ifd.t322 !== undefined && ifd.t323 !== undefined && ifd.t324 !== undefined && ifd.t325 !== undefined
  if (!hasCompleteStrips || hasCompleteStrips === hasCompleteTiles) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }
}

function validateUncompressedChunkPayload(
  ifd: TiffIfd,
  dimensions: { width: number; height: number },
  layout: TiffPixelLayout,
  errors: TiffErrors,
): void {
  const compression = readFirstNumericTag(ifd.t259, 1)
  if (compression !== 1) return

  const offsets = readNumericTag(ifd.t273)
  const byteCounts = readNumericTag(ifd.t279)
  if (ifd.t273 === undefined && ifd.t279 === undefined) return
  if (offsets === null || byteCounts === null || offsets.length !== byteCounts.length) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }

  const declaredRowsPerStrip = readFirstNumericTag(ifd.t278, dimensions.height)
  if (!Number.isSafeInteger(declaredRowsPerStrip) || declaredRowsPerStrip <= 0) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }
  // TIFF permits RowsPerStrip to exceed ImageLength. It still represents one
  // complete-image strip, so bound it to the rows the decoder can produce.
  const rowsPerStrip = Math.min(declaredRowsPerStrip, dimensions.height)
  const expectedStripCount = Math.ceil(dimensions.height / rowsPerStrip)
  if (offsets.length !== expectedStripCount) {
    throw new TiffConversionError(errors.corrupt, 'corrupt_image')
  }

  const bitsPerRow = dimensions.width * layout.samples * layout.bitsPerSample
  const bytesPerRow = Math.ceil(bitsPerRow / 8)
  for (let stripIndex = 0; stripIndex < expectedStripCount; stripIndex += 1) {
    const offset = offsets[stripIndex]
    const byteCount = byteCounts[stripIndex]
    const rows = Math.min(rowsPerStrip, dimensions.height - stripIndex * rowsPerStrip)
    const expectedBytes = rows * bytesPerRow
    if (
      offset === undefined ||
      byteCount === undefined ||
      !Number.isSafeInteger(offset) ||
      !Number.isSafeInteger(byteCount) ||
      offset < 0 ||
      byteCount !== expectedBytes
    ) {
      throw new TiffConversionError(errors.corrupt, 'corrupt_image')
    }
  }
}

interface TiffPixelLayout {
  photometric: number
  bitsPerSample: number
  samples: number
}

function readUniformPixelLayout(ifd: TiffIfd): TiffPixelLayout | null {
  const bits =
    ifd.t258 === undefined ? [1] : readNumericTag(ifd.t258)
  if (
    !bits ||
    bits.length === 0 ||
    bits.some(
      (value) => !Number.isFinite(value) || !Number.isInteger(value) || value <= 0,
    ) ||
    !bits.every((value) => value === bits[0])
  ) {
    return null
  }

  // TIFF 6.0 specifies SamplesPerPixel=1 when the tag is absent.
  const samples = readFirstNumericTag(ifd.t277, 1)
  if (
    !Number.isFinite(samples) ||
    !Number.isInteger(samples) ||
    samples <= 0 ||
    bits.length !== samples
  ) {
    return null
  }

  const photometric = readFirstNumericTag(ifd.t262, 2)
  if (!Number.isFinite(photometric) || !Number.isInteger(photometric)) return null

  return {
    photometric,
    bitsPerSample: bits[0] ?? 0,
    samples,
  }
}

function isCommonSafeLayout(ifd: TiffIfd, layout: TiffPixelLayout): boolean {
  const { photometric, bitsPerSample, samples } = layout

  const sampleFormat = ifd.t339 === undefined
    ? Array.from({ length: samples }, () => 1)
    : readNumericTag(ifd.t339)
  if (
    readFirstNumericTag(ifd.t266, 1) !== 1 ||
    readFirstNumericTag(ifd.t274, 1) !== 1 ||
    sampleFormat === null ||
    sampleFormat.length !== samples ||
    sampleFormat.some((value) => value !== 1)
  ) {
    return false
  }

  const extraSamples = readNumericTag(ifd.t338)
  const hasUnassociatedAlpha =
    (photometric === 2 && samples === 4) ||
    (photometric === 5 && samples === 5)
  if (hasUnassociatedAlpha) {
    if (extraSamples?.length !== 1 || extraSamples[0] !== 2) return false
  } else if (extraSamples !== null) {
    return false
  }

  if (bitsPerSample !== 8) return false
  if (photometric === 0 || photometric === 1) return samples === 1
  if (photometric === 2) return samples === 3 || samples === 4
  if (photometric === 3) {
    const colorMap = readNumericTag(ifd.t320)
    return samples === 1 && colorMap !== null && colorMap.length === 3 * 2 ** 8
  }
  return photometric === 5 && (samples === 4 || samples === 5)
}

function isSupportedCompressionLayout(
  ifd: TiffIfd,
  compression: number,
  layout: TiffPixelLayout,
): boolean {
  if (COMMON_SAFE_COMPRESSIONS.has(compression)) {
    return isCommonSafeLayout(ifd, layout)
  }
  // UTIF's synchronous decoders for these codecs do not enforce bounded
  // decoded output or iteration counts. Reject them until they can be
  // preflighted or isolated in a terminating worker.
  if (CCITT_COMPRESSIONS.has(compression)) return false
  if (JPEG_COMPRESSIONS.has(compression)) return false
  if (compression === THUNDERSCAN_COMPRESSION) return false
  return false
}

function validateSupportedTiffVariant(ifd: TiffIfd, errors: TiffErrors): void {
  const compression = readFirstNumericTag(ifd.t259, 1)
  const layout = readUniformPixelLayout(ifd)
  const isChunkyLayout = readFirstNumericTag(ifd.t284, 1) === 1

  if (
    !Number.isFinite(compression) ||
    !Number.isInteger(compression) ||
    !isChunkyLayout ||
    !layout ||
    !isSupportedCompressionLayout(ifd, compression, layout)
  ) {
    throw new TiffConversionError(errors.unsupportedVariant, 'unsupported_variant')
  }
}

export function validateTiffMetadata(
  ifd: TiffIfd,
  errors: TiffErrors,
): { width: number; height: number } {
  const dimensions = validateDimensionsBeforeDecode(ifd, errors)
  validateStorageLayout(ifd, errors)
  validateSupportedTiffVariant(ifd, errors)
  validateDecodeMemoryBudget(ifd, dimensions, errors)
  const layout = readUniformPixelLayout(ifd)
  if (layout) validateUncompressedChunkPayload(ifd, dimensions, layout, errors)
  return dimensions
}

function compositeRgbaOnWhite(rgba: Uint8Array): Uint8ClampedArray {
  for (let index = 0; index < rgba.length; index += 4) {
    const alpha = (rgba[index + 3] ?? 255) / 255
    rgba[index] = Math.round((rgba[index] ?? 0) * alpha + 255 * (1 - alpha))
    rgba[index + 1] = Math.round((rgba[index + 1] ?? 0) * alpha + 255 * (1 - alpha))
    rgba[index + 2] = Math.round((rgba[index + 2] ?? 0) * alpha + 255 * (1 - alpha))
    rgba[index + 3] = 255
  }

  return new Uint8ClampedArray(rgba.buffer, rgba.byteOffset, rgba.byteLength)
}

export function encodeJpegFromRgba(
  rgba: Uint8Array,
  width: number,
  height: number,
  quality: number,
  errors: TiffErrors,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) {
      reject(new TiffConversionError(errors.canvas, 'canvas_error'))
      return
    }

    context.putImageData(new ImageData(compositeRgbaOnWhite(rgba), width, height), 0, 0)
    canvas.toBlob(
      (blob) => {
        if (blob?.type === 'image/jpeg') {
          resolve(blob)
          return
        }
        reject(new TiffConversionError(errors.conversion, 'conversion_failed'))
      },
      'image/jpeg',
      quality / 100,
    )
  })
}

export async function decodeTiffToRgba(
  file: File,
  errors: TiffErrors,
): Promise<{ rgba: Uint8Array; frames: number; width: number; height: number }> {
  const buffer = await file.arrayBuffer()
  const header = readTiffHeader(buffer)
  if (header === 'big_tiff') throw new TiffConversionError(errors.unsupportedVariant, 'unsupported_variant')
  if (header === 'invalid') throw new TiffConversionError(errors.invalidSignature, 'invalid_signature')
  const firstImageCompression = validateClassicTiffIfdChain(buffer, errors)
  if (firstImageCompression === 8) throw new TiffConversionError(errors.unsupportedVariant, 'unsupported_variant')
  const { default: UTIF } = await import('utif')
  const ifds = UTIF.decode(buffer)
  const imageIfds = ifds.filter(hasImageTags)
  const firstImage = imageIfds[0]
  if (ifds.length === 0 || !firstImage) throw new TiffConversionError(errors.noImages, 'no_images')
  const { width, height } = validateTiffMetadata(firstImage, errors)
  UTIF.decodeImage(buffer, firstImage)
  return { rgba: UTIF.toRGBA8(firstImage), frames: imageIfds.length, width, height }
}

type TiffWorkerResult =
  | { ok: true; blob: Blob; frames: number; width: number; height: number }
  | { ok: false; message: string; code: TiffErrorCode }

async function convertTiffInWorker(file: File, quality: number, errors: TiffErrors): Promise<TiffDecodeResult> {
  const worker = new Worker(new URL('./tiffWorker.ts', import.meta.url), { type: 'module' })
  const operation = new PendingTiffOperation()
  operation.worker = worker
  activeTiffOperations.add(operation)
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      worker.terminate()
      activeTiffOperations.delete(operation)
      operation.settleCancellation = null
    }
    const stopWorker = () => worker.terminate()
    const cancel = () => {
      cleanup()
      reject(new TiffConversionError(errors.decode, 'decode_failed'))
    }
    operation.settleCancellation = cancel
    void (async () => {
      const buffer = await file.arrayBuffer()
      if (operation.cancelled) return
      worker.onmessage = (event: MessageEvent<TiffWorkerResult>) => {
        stopWorker()
        const result = event.data
        if (!result.ok) {
          cleanup()
          reject(new TiffConversionError(result.message, result.code))
          return
        }
        if (result.blob) {
          if (result.blob.type !== 'image/jpeg') {
            cleanup()
            reject(new TiffConversionError(errors.conversion, 'conversion_failed'))
            return
          }
          cleanup()
          resolve({ blob: result.blob, frames: result.frames, width: result.width, height: result.height })
          return
        }
        cleanup()
        reject(new TiffConversionError(errors.conversion, 'conversion_failed'))
      }
      worker.onerror = () => {
        cleanup()
        reject(new TiffConversionError(errors.decode, 'decode_failed'))
      }
      worker.postMessage({ buffer, quality, errors }, [buffer])
    })().catch((error) => {
      if (operation.cancelled) return
      cleanup()
      reject(error)
    })
  })
}

export function cancelPendingTiffConversions(): void {
  for (const operation of [...activeTiffOperations]) operation.cancel()
}

export async function convertTiffToJpg(
  file: File,
  quality: number,
  errors: TiffErrors,
): Promise<TiffDecodeResult> {
  try {
    if (typeof Worker === 'undefined') {
      throw new TiffConversionError(errors.unsupportedVariant, 'unsupported_variant')
    }
    return await convertTiffInWorker(file, quality, errors)
  } catch (error) {
    if (error instanceof TiffConversionError) {
      throw error
    }
    if (looksLikeMemoryFailure(error)) {
      throw new TiffConversionError(errors.memory, 'memory_failed')
    }
    throw new TiffConversionError(errors.decode, 'decode_failed')
  }
}
